import { Box,
  Button, Center, Checkbox, CheckboxGroup, Collapse,
  Container, Flex ,
  FormControl, FormErrorMessage,
  FormLabel, Grid, GridItem, Heading, HStack, Input, Radio, RadioGroup,
  Spinner, Text, useDisclosure, useToast, VStack
} from "@chakra-ui/react";
import {AxiosResponse} from "axios";
import { Field, Form, Formik } from "formik";
import React, {useEffect, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "react-query";
import {Navigate, useLocation, useNavigate} from "react-router-dom";

import {getCurrentUser as apiGetCurrentUser, SATSurveyCompleted, submitSATSurvey} from "../Api";
import {errorToast, successToast} from "../helper/ToastHelper";
import {
  useAuthentication,
} from "../hooks/AuthenticationProvider";
import { disableBlurProtection } from "../hooks/BlurProtection";
import {useTheme} from "../hooks/ThemeProvider";
import {Paths, UserStatus} from "../model/Constants";
import {UserDto} from "../model/Dto";
import {userKeys} from "../QueryKeys";
import {enableKeyCommands, extractData, isInAvataiSite} from "../Utils";

const CATEGORIES = [
  { value: "1", label: "Muy en desacuerdo" },
  { value: "2", label: "Algo en desacuerdo" },
  { value: "3", label: "Ni de acuerdo ni en desacuerdo" },
  { value: "4", label: "Algo de acuerdo" },
  { value: "5", label: "Muy de acuerdo" }
];

const QUESTIONS = [
  { id: "SATq1", text: "La plataforma del examen es fácil de utilizar." },
  { id: "SATq2", text: "La plataforma es clara." },
  { id: "SATq3", text: "El examen inició puntualmente a la hora convocada. " },
  { id: "SATq4", text: "Las instrucciones proporcionadas fueron claras para realizar el examen." },
  { id: "SATq5", text: "La resolución de problemas técnicos fue eficiente." },
  { id: "SATq6", text: "Las dudas y consultas fueron resueltas de manera clara." },
  { id: "SATq7", text: "La persona que supervisó la prueba estaba atenta al grupo." },
  { id: "SATq8", text: "La aplicación del examen fue justa para todas las personas que realizaron el examen al mismo tiempo que yo." },
  { id: "SATq9", text: "Recibí un trato amable y agradable por parte de la persona encargada del examen." },
  { id: "SATq10", text: "En general, considero que mi experiencia fue satisfactoria realizando el examen." }
];

const SATSurvey = () => {
  const toast = useToast();
  const authentication = useAuthentication();
  const location = useLocation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const theme = useTheme().theme;


  const surveyQuery = useQuery(userKeys.SATSurvey(), () =>
  { return SATSurveyCompleted().then(extractData); });

  const [answers, setAnswers] = useState<Record<string, string>>({});

  const handleRadioChange = (questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const [isError, setIsError] = useState(true);


  if ( isInAvataiSite())
    navigate(Paths.HOME, { replace: true });

  disableBlurProtection();
  enableKeyCommands();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Submitted Answers:", answers);
  };

  const submitForm = useMutation<AxiosResponse<boolean>, Error, Record<string, string>>(
    (params: Record<string, string>) => {
      return submitSATSurvey(params);
    },
    {
      onSuccess: () => {
        successToast(toast, `Encuesta enviada exitosamente`);
        return queryClient.invalidateQueries(userKeys.SATSurvey());
      },
      onError: () => {
        errorToast(toast, "Ocurrió un error en el servidor");
      },
    }
  );

  if (
    !authentication.currentUserQuery ||
    authentication.currentUserQuery.isLoading ||
    surveyQuery.isLoading
  ) {
    return <Spinner />;
  } else if (surveyQuery.data!==null && surveyQuery.data ===true){
    return <Navigate to={(Paths.HOME)}  />;
  } else  return (
    <Container maxW="container.lg">

        <form  onSubmit={(e: React.FormEvent) =>{
                          e.preventDefault();
                          submitForm.mutate(answers)
                      }    }>
          <Heading paddingBlock='4' as='h4' size='lg'>Encuesta de satisfacción</Heading>

          <Grid templateColumns="3fr repeat(5, 1fr)" gap={4} alignItems="center">

            <GridItem display={{ base: "none", md: "block" }} />

            {CATEGORIES.map((cat) => (
            <GridItem key={cat.value} textAlign="center" display={{ base: "none", md: "block" }}>
              <Text fontWeight="bold">
                {cat.label}
              </Text>
            </GridItem>
            ))}

            {QUESTIONS.map((q) => (
            <React.Fragment key={q.id}>

              <GridItem colSpan={{ base: 6, md: 1 }} py={2}>
                <Text fontWeight="normal">{q.text}</Text>
              </GridItem>

              <GridItem colSpan={{ base: 6, md: 5 }} py={2}>
              <FormControl isRequired>
                <RadioGroup
                  value={answers[q.id] || ""}
                  onChange={(value) => handleRadioChange(q.id, value)}
                >
                  <Flex
                    direction={{ base: "column", md: "row" }}
                    justify="space-around"
                    gap={{ base: 2, md: 0 }}
                  >
                    {CATEGORIES.map((cat) => (
                      <Flex
                        key={cat.value}
                        flex="1"
                        justify="center"
                        align="center"
                        direction={{ base: "row-reverse", md: "row" }}
                        justifyContent={{ base: "space-between", md: "center" }}
                        w="100%"
                      >
                        {/* Mobile view fallback label */}
                        <Text display={{ base: "block", md: "none" }} fontSize="sm" color="gray.600">
                          {cat.label}
                        </Text>

                        <Radio  size='lg' borderColor="black"
                                                borderWidth="2px"
                                                value={cat.value} aria-label={`${q.text} - ${cat.label}`} />
                      </Flex>
                    ))}
                  </Flex>
                </RadioGroup>
              </FormControl>
              </GridItem>

              <GridItem colSpan={6} borderBottom="1px solid" borderColor="black" my={1} />
            </React.Fragment>
            ))}
          </Grid>





          <Center>
            <Button
              mt={4}
              margin={5}
              colorScheme="teal"
              type="submit"
              isLoading={authentication.loginMutation?.isLoading}
              _disabled={{
                cursor: 'default',
                opacity: '0.4',
              }}
            >
              Confirmar
            </Button>
          </Center>
        </form >
    </Container>
  );
};

export default SATSurvey;
